import React, { useState, useRef, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import {
  Save, Download, ChevronDown, FileText, Image as ImageIcon,
  Undo, Redo, Copy, Square, Diamond, Home,
  LayoutDashboard, Settings2, ArrowRight, FolderOpen, Activity, Play,
  Trash2, MousePointer2, Circle, Palette, LogOut,
  Cloud, Magnet, FileSpreadsheet, PlayCircle, StopCircle,
  FileDown, HardDrive, FileUp, LayoutTemplate, Printer,
  BarChart3, Scale, EyeOff, Type, ArrowDown, ArrowUp, Users, Flame,
  Workflow, Award, StickyNote, Lightbulb, AlertCircle, Target, Database, ShieldAlert, Rows3,
  MoreHorizontal, Rocket, CheckCircle2, Eye, Pencil, Search, SlidersHorizontal, Layers,
  ChevronLeft, ChevronRight, X,
  Contact, Minimize2, Factory, Zap, Compass, Box, Ticket,
  Grid3X3, Ruler, Maximize2, Hand, PanelBottom, GitMerge, KanbanSquare,
  LayoutPanelLeft, ListChecks, Map, ZoomIn, ZoomOut, FilePlus, TableProperties
} from 'lucide-react';
import { PostItType } from '../types';
import { LayoutType } from './ViewToggle';
import { MetricsInline } from './MetricsSummary';
import { ViewModeSwitcher, ViewMode } from './ViewModeSwitcher';
import { useProjectStore } from '../store/useProjectStore';
import {
  desktopRibbonDividerClass,
  desktopRibbonGroupClass,
  desktopRibbonInputClass,
  RibbonDesktopButton,
} from './ribbon/DesktopRibbonControls';

// --- Tipos e Props ---
export interface RibbonProps {
  onSave?: () => void;
  onSaveLocal?: () => void;
  onOpenLocal?: () => void;
  onNewMap?: () => void;
  onImportExcel?: () => void;
  onDownloadTemplate?: () => void;
  onExportPDF?: () => void;
  onExportEnterpriseReport?: () => void;
  onExportTechnicalFlowReport?: () => void;
  onExportExcel?: () => void;
  onExportImage?: () => void;
  onExportA3?: () => void;
  onExportLeanReport?: () => void;
  onOpenReportStudio?: () => void;
  onUndo?: () => void;
  onRedo?: () => void;
  onAddActivity?: () => void;
  onAddDecision?: () => void;
  onAddStart?: () => void;
  onAddFinalCard?: () => void;
  onAddPostIt?: (type: PostItType) => void;
  onToggleGrid?: () => void;
  gridActive?: boolean;
  onToggleSnap?: () => void;
  snapActive?: boolean;
  onToggleRulers?: () => void;
  rulersActive?: boolean;
  onToggleHeatmap?: () => void;
  heatmapActive?: boolean;
  onTogglePageMode?: (format?: 'a4-portrait' | 'a4-landscape' | 'a3-portrait' | 'a3-landscape') => void;
  pageModeActive?: boolean;
  pageFormat?: 'a4-portrait' | 'a4-landscape' | 'a3-portrait' | 'a3-landscape';
  onAddPageConnector?: (direction: 'out' | 'in') => void;
  onContentScaleChange?: (scale: number) => void;
  contentScale?: number;
  onResetZoom?: () => void;
  onZoomIn?: () => void;
  onZoomOut?: () => void;
  currentZoomLabel?: string;
  onTogglePanMode?: () => void;
  panModeActive?: boolean;
  onShowDashboard?: () => void;
  onShowProperties?: () => void;
  onShowKaizen?: () => void;
  onShowYamazumi?: () => void;
  onLayoutChange?: (layout: LayoutType) => void;
  currentLayout?: LayoutType;
  onColorChange?: (color: string) => void;
  currentColor?: string;
  onToggleManualArrow?: () => void;
  manualArrowMode?: boolean;
  onDeleteSelected?: () => void;
  onDuplicateSelected?: () => void;
  onToggleAnalysisMode?: () => void;
  analysisModeActive?: boolean;
  onToggleSimulation?: () => void;
  simulationActive?: boolean;
  onToggleExecutiveView?: () => void;
  executiveViewActive?: boolean;
  onToggleMinimap?: () => void;
  minimapActive?: boolean;
  onToggleWidget?: () => void;
  widgetActive?: boolean;
  yamazumiActive?: boolean;
  onVisualSettings?: () => void;
  onOrgManager?: () => void;
  onProjectSettings?: () => void;
  // Abre o modal de parâmetros da fábrica (substitui engrenagem flutuante)
  onOpenFactorySettings?: () => void;
  // Estado Atual / Futuro / COMPARAÇÃO
  onCreateCurrentState?: () => void;
  onCreateFutureState?: () => void;
  onCompareScenarios?: () => void;
  comparisonAvailable?: boolean;
  comparisonActive?: boolean;
  activeState?: 'atual' | 'futuro' | 'comparar';
  user?: any;
  onLogin?: () => void;
  onLogout?: () => void;
  goHome?: () => void;
  projectName?: string;
  isCloudProject?: boolean;
  // View Mode Switcher
  viewMode?: ViewMode;
  onViewModeChange?: (mode: ViewMode) => void;
  // Props do Journey Stepper
  currentProjectStep?: string;
  isJourneySidebarOpen?: boolean;
  onToggleJourneySidebar?: () => void;
  journeyDeviationAlert?: boolean;
  projectMode?: 'full' | 'simple';
  currentViewMode?: 'current_clean' | 'kaizen_work' | 'future_simulated';
  // Seleção múltipla de cards
  selectedCardIds?: Set<string>;
  selectedCardId?: string | null;
  selectedCardType?: string;
  onAddCardConnected?: (type: 'atividade' | 'decisao', isYesPath?: boolean) => void;
  contentInsetLeft?: number;
  // Swimlanes toggle
  onToggleSwimlanes?: () => void;
  swimlanesActive?: boolean;
  // Mapa Vivo (Live Map)
  onToggleLiveMode?: () => void;
  liveModeActive?: boolean;
  // Comparação Mapa vs. Realidade
  onShowComparePanel?: () => void;
  comparePanelActive?: boolean;
  // Mapa pronto para execução (contextual Finalizar)
  isMapReady?: boolean;
  // Cards disponíveis para detectar readiness
  cards?: Array<{ tipo?: string }>;
  // Blueprint vinculado (habilita Comparar Execução)
  linkedBlueprintId?: string | null;
  // Pesquisa e filtro no ribbon (opcional)
  onSearch?: (q: string) => void;
  /** Busca controlada externamente (ex.: filtro da lista de fluxos) */
  searchValue?: string;
  searchPlaceholder?: string;
  searchResultCount?: number;
  searchResultIndex?: number;
  onSearchNext?: () => void;
  onSearchPrev?: () => void;
  onSearchClear?: () => void;
  // VSM Canvas Híbrido
  onOpenVsm?: () => void;
  // Factory VSM mode
  mapType?: string;
  vsmDesignStyle?: 'blueprint' | 'sketch' | 'technical';
  onVsmDesignStyleChange?: (style: 'blueprint' | 'sketch' | 'technical') => void;
  vsmCardColor?: string;
  onVsmCardColorChange?: (color: string) => void;
  /** When true, all write/mutation controls are disabled (Visualizador role) */
  readOnly?: boolean;
  /** Mobile: exibe apenas controles de visualização/zoom */
  mobileViewOnly?: boolean;
  /** Alternância de aba do módulo Mapa: 'list' (Central de Fluxos) | 'canvas' (VSM) */
  activeMapaTab?: 'list' | 'canvas';
  onMapaTabChange?: (tab: 'list' | 'canvas') => void;
  /** Filtros extras renderizados à esquerda da busca (ex.: lista de fluxos) */
  filtersSlot?: React.ReactNode;
}

// --- Separador Vertical Discreto ---
const Separator = () => <div className={desktopRibbonDividerClass} />;
const SectionSeparator: React.FC<{ gap?: number }> = ({ gap }) => (
  <div className={desktopRibbonDividerClass} style={gap ? { marginLeft: `${gap}px`, marginRight: `${gap}px` } : undefined} />
);

const RibbonGroup: React.FC<React.PropsWithChildren<{ label: string }>> = ({ label: _label, children }) => (
  <div
    className="enterprise-ribbon-group flex h-full shrink-0 items-center pr-2"
    style={{ borderRight: '1px solid var(--border-subtle, rgba(0,0,0,0.08))', gap: '6px' }}
  >
    {children}
  </div>
);

// --- Botão da Toolbar (Limpo e Sem Cores Fixas) ---
interface ToolBtnProps {
  icon: React.ReactNode;
  label?: string;
  onClick?: () => void;
  active?: boolean;
  disabled?: boolean;
  hasDropdown?: boolean;
  className?: string;
  priority?: 'primary' | 'secondary' | 'tertiary';
  activeStyle?: 'default' | 'toggle';
  title?: string;
}

const ToolBtn: React.FC<ToolBtnProps> = ({
  icon,
  label,
  onClick,
  active,
  disabled,
  hasDropdown,
  className,
  priority = 'secondary',
  activeStyle = 'default',
  title,
}) => {
  void priority;
  void activeStyle;
  return (
    <RibbonDesktopButton
      icon={icon}
      label={label ?? ''}
      onClick={onClick}
      active={active}
      disabled={disabled}
      title={title || label}
      className={className}
      hasDropdown={hasDropdown}
    />
  );
};

/** Shared icon style for dropdown menu items */
const DI: React.FC<{ icon: React.ReactNode }> = ({ icon }) => (
  <span className="mr-2 shrink-0 text-[var(--text-secondary)] [&_svg]:h-[13px] [&_svg]:w-[13px]">{icon}</span>
);

/** Empty spacer that reserves the same horizontal slot as a DI icon, used by text-only items to keep text aligned with iconed siblings */
const DISpacer: React.FC = () => (
  <span className="mr-2 shrink-0 inline-block h-[13px] w-[13px]" aria-hidden="true" />
);

const dropdownPanelClass =
  'ribbon-dropdown-panel enterprise-font fixed z-[9999] overflow-hidden rounded-[4px] border border-[var(--border-default)] bg-[var(--bg-elevated)] px-0 py-1 text-[var(--text-primary)] shadow-xl animate-in fade-in duration-100';
const dropdownBaseButtonClass =
  'w-full h-[26px] rounded-none border-l-[3px] border-l-transparent pl-8 pr-2.5 py-[3px] text-left text-[13px] font-normal leading-[1.2] text-[var(--text-primary)] transition-colors duration-100 hover:bg-[var(--button-hover)]';
const dropdownActiveButtonClass = 'border-l-[var(--nav-active-bar)] bg-[var(--brand-primary-soft)] text-[var(--text-primary)] font-medium';
const dropdownInactiveButtonClass = '';
const dropdownSectionLabelClass =
  'px-2.5 pt-2 pb-1 text-[11px] font-semibold uppercase leading-[1.1] tracking-[0.05em] text-[var(--text-secondary)] first:pt-0';

const DropdownMenu: React.FC<
  React.PropsWithChildren<{
    top: number;
    left: number;
    width: number | string;
    onMouseEnter: () => void;
    onMouseLeave: () => void;
    className?: string;
  }>
> = ({ top, left, width, onMouseEnter, onMouseLeave, className = '', children }) => (
  <div
    className={`${dropdownPanelClass} ${className}`}
    style={{ position: 'fixed', top: `${top}px`, left: `${left}px`, width }}
    onMouseEnter={onMouseEnter}
    onMouseLeave={onMouseLeave}
  >
    {children}
  </div>
);

const DropdownSeparator = () => <div className="my-1 h-px bg-[var(--border-subtle)]" />;

const DropdownSectionTitle: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className={dropdownSectionLabelClass}>
    {children}
  </div>
);

/** Flyout submenu item — shows a panel to the right on hover */
const SubMenuItem: React.FC<{
  label: string;
  icon?: React.ReactNode;
  children: React.ReactNode;
  onCloseAll: () => void;
}> = ({ label, icon, children, onCloseAll }) => {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const rowRef = useRef<HTMLDivElement>(null);

  const handleEnter = () => {
    if (rowRef.current) {
      const r = rowRef.current.getBoundingClientRect();
      setPos({ top: r.top - 4, left: r.right + 2 });
    }
    setOpen(true);
  };

  return (
    <div
      ref={rowRef}
      className="relative"
      onMouseEnter={handleEnter}
      onMouseLeave={() => setOpen(false)}
    >
      <div
        className={`${dropdownBaseButtonClass} flex items-center justify-between gap-1 cursor-default`}
      >
        <span className="flex items-center gap-1.5 truncate">
          {icon && <span className="mr-2 shrink-0 text-[var(--text-secondary)] [&_svg]:h-[13px] [&_svg]:w-[13px]">{icon}</span>}
          {label}
        </span>
        <ChevronRight size={11} className="shrink-0 text-[var(--text-secondary)]" />
      </div>

      {open && createPortal(
        <div
          className={dropdownPanelClass}
          style={{ position: 'fixed', top: `${pos.top}px`, left: `${pos.left}px`, width: '220px' }}
          onMouseEnter={() => setOpen(true)}
          onMouseLeave={() => setOpen(false)}
          onClick={onCloseAll}
        >
          {children}
        </div>,
        document.body
      )}
    </div>
  );
};

interface DropdownItemProps {
  children: React.ReactNode;
  onClick?: () => void;
  active?: boolean;
  disabled?: boolean;
  rightSlot?: React.ReactNode;
  monoRight?: boolean;
  className?: string;
}

const DropdownItem: React.FC<DropdownItemProps> = ({
  children,
  onClick,
  active = false,
  disabled = false,
  rightSlot,
  monoRight = false,
  className = '',
}) => (
  <button
    onClick={disabled ? undefined : onClick}
    disabled={disabled}
    className={[
      'relative flex w-full items-center justify-between gap-3 overflow-hidden',
      dropdownBaseButtonClass,
      active ? dropdownActiveButtonClass : dropdownInactiveButtonClass,
      disabled ? 'cursor-not-allowed opacity-35' : '',
      className,
    ].join(' ')}
  >
    <span className="truncate">{children}</span>
    {rightSlot && (
      <span
        className={`shrink-0 text-[12px] text-[var(--text-secondary)] ${monoRight ? 'font-mono tabular-nums tracking-[0.01em]' : ''}`}
      >
        {rightSlot}
      </span>
    )}
  </button>
);

// ─────────────────────────────────────────────────────────────────────────────
// V234 — Premium Sidebar Style Menu (Design dropdown)
// Componentes modulares para o menu de Design no estilo Figma/Notion.
// Cabeçalho silencioso, item ativo com barra ciana à esquerda, cores em accordion.
// ─────────────────────────────────────────────────────────────────────────────

const premiumPanelClass =
  'enterprise-font fixed z-[9999] overflow-hidden rounded-[4px] border border-[var(--border-default)] bg-[var(--bg-elevated)] shadow-xl py-1 animate-in fade-in duration-100';

const PremiumSectionHeader: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="px-2.5 pt-2 pb-1 text-[11px] font-semibold uppercase leading-[1.1] tracking-[0.05em] text-[var(--text-secondary)] first:pt-1">
    {children}
  </div>
);

const PremiumStyleItem: React.FC<{
  title: string;
  icon?: React.ReactNode;
  active?: boolean;
  onClick?: () => void;
}> = ({ title, icon, active, onClick }) => (
  <button
    onClick={onClick}
    className={`w-full h-[26px] text-left text-[13px] leading-[1.2] flex items-center gap-2 pr-2.5 transition-colors hover:bg-[var(--button-hover)] ${
      active
        ? 'border-l-[3px] border-l-cyan-500 bg-cyan-50/60 dark:bg-cyan-900/15 text-[var(--text-primary)] font-medium pl-[7px]'
        : 'border-l-[3px] border-l-transparent text-[var(--text-primary)] pl-[7px]'
    }`}
  >
    {icon && (
      <span className={`shrink-0 ${active ? 'text-cyan-600 dark:text-cyan-400' : 'text-[var(--text-secondary)]'} [&_svg]:h-[13px] [&_svg]:w-[13px]`}>
        {icon}
      </span>
    )}
    <span className="truncate">{title}</span>
  </button>
);

const PremiumColorAccordion: React.FC<{
  label: string;
  colors: string[];
  currentColor?: string;
  onSelect: (color: string) => void;
  onCustom?: () => void;
}> = ({ label, colors, currentColor, onSelect, onCustom }) => {
  const [open, setOpen] = useState(false);
  return (
    <div>
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full h-[26px] text-left text-[13px] leading-[1.2] flex items-center gap-2 pr-2.5 pl-[7px] border-l-[3px] border-l-transparent transition-colors hover:bg-[var(--button-hover)] text-[var(--text-primary)]"
      >
        <span className="shrink-0 text-[var(--text-secondary)]">
          <Palette size={13} strokeWidth={1.5} />
        </span>
        <span className="flex-1 truncate">{label}</span>
        {currentColor && (
          <span
            className="h-3 w-3 rounded-sm border border-[var(--border-subtle)] shrink-0"
            style={{ backgroundColor: currentColor }}
          />
        )}
        <ChevronRight size={11} className={`text-[var(--text-secondary)] shrink-0 transition-transform ${open ? 'rotate-90' : ''}`} />
      </button>
      {open && (
        <div className="bg-[var(--button-hover)]/40">
          <div className="grid grid-cols-6 gap-[5px] px-2.5 py-2">
            {colors.map(color => (
              <button
                key={color}
                title={color}
                onClick={() => onSelect(color)}
                style={{ backgroundColor: color }}
                className={`h-5 w-5 rounded-[3px] transition-transform hover:scale-110 ${
                  currentColor === color
                    ? 'ring-1 ring-cyan-500 ring-offset-1 ring-offset-[var(--bg-surface)]'
                    : 'border border-black/15 dark:border-white/15'
                }`}
              />
            ))}
          </div>
          {onCustom && (
            <button
              onClick={onCustom}
              className="w-full text-left h-[24px] pl-[10px] pr-2.5 text-[12px] leading-[1.2] text-[var(--text-primary)] hover:bg-[var(--button-hover)] flex items-center gap-2 border-t border-[var(--border-subtle)]"
            >
              <Palette size={11} strokeWidth={1.5} className="text-[var(--text-secondary)]" />
              Cor personalizada…
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export const Ribbon: React.FC<RibbonProps> = (props) => {
  const saveStatus = useProjectStore((state) => state.saveStatus);
  const isFactoryMap = props.mapType === 'FACTORY';
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const [hoveringDropdown, setHoveringDropdown] = useState(false);
  const [ribbonSearch, setRibbonSearch] = useState('');
  const isSearchControlled = props.searchValue !== undefined;
  const effectiveSearch = isSearchControlled ? (props.searchValue ?? '') : ribbonSearch;
  const menuRef = useRef<HTMLDivElement>(null);
  
  // Refs para os botões com dropdown
  const fileButtonRef = useRef<HTMLDivElement>(null);
  const postItButtonRef = useRef<HTMLDivElement>(null);
  const layoutButtonRef = useRef<HTMLDivElement>(null);
  const colorButtonRef = useRef<HTMLDivElement>(null);
  const colorInputRef = useRef<HTMLInputElement>(null);
  const userButtonRef = useRef<HTMLDivElement>(null);
  const analysisButtonRef = useRef<HTMLDivElement>(null);
  const pageModeButtonRef = useRef<HTMLDivElement>(null);
  const contentScaleButtonRef = useRef<HTMLDivElement>(null);
  const overflowButtonRef = useRef<HTMLDivElement>(null);
  const finalizarButtonRef = useRef<HTMLDivElement>(null);
  const modeButtonRef = useRef<HTMLDivElement>(null);
  const projetoButtonRef = useRef<HTMLDivElement>(null);
  const exibirButtonRef = useRef<HTMLDivElement>(null);
  
  // Estados para posicionamento dos dropdowns
  const [filePosition, setFilePosition] = useState({ top: 0, left: 0 });
  const [postItPosition, setPostItPosition] = useState({ top: 0, left: 0 });
  const [layoutPosition, setLayoutPosition] = useState({ top: 0, left: 0 });
  const [userPosition, setUserPosition] = useState({ top: 0, left: 0 });
  const [analysisPosition, setAnalysisPosition] = useState({ top: 0, left: 0 });
  const [pageModePosition, setPageModePosition] = useState({ top: 0, left: 0 });
  const [contentScalePosition, setContentScalePosition] = useState({ top: 0, left: 0 });
  const [overflowPosition, setOverflowPosition] = useState({ top: 0, left: 0 });
  const [finalizarPosition, setFinalizarPosition] = useState({ top: 0, left: 0 });
  const [modePosition, setModePosition] = useState({ top: 0, left: 0 });
  const [projetoPosition, setProjetoPosition] = useState({ top: 0, left: 0 });
  const [exibirPosition, setExibirPosition] = useState({ top: 0, left: 0 });

  // Detectar se o mapa está "pronto" para execução
  const isReady = useMemo(() => {
    if (props.isMapReady !== undefined) return props.isMapReady;
    if (!props.cards) return false;
    const hasStart = props.cards.some(c => c.tipo === 'card_inicial');
    const hasEnd = props.cards.some(c => c.tipo === 'card_final');
    const hasActivities = props.cards.some(c => c.tipo === 'atividade');
    return hasStart && hasEnd && hasActivities;
  }, [props.isMapReady, props.cards]);

  // Calcular posições dos dropdowns
  useEffect(() => {
    const updatePositions = () => {
      if (openDropdown === 'file' && fileButtonRef.current) {
        const rect = fileButtonRef.current.getBoundingClientRect();
        setFilePosition({ top: rect.bottom, left: rect.left });
      }
      if (openDropdown === 'postits' && postItButtonRef.current) {
        const rect = postItButtonRef.current.getBoundingClientRect();
        setPostItPosition({ top: rect.bottom, left: rect.left });
      }
      if (openDropdown === 'layout' && layoutButtonRef.current) {
        const rect = layoutButtonRef.current.getBoundingClientRect();
        setLayoutPosition({ top: rect.bottom, left: rect.left });
      }
      if (openDropdown === 'user' && userButtonRef.current) {
        const rect = userButtonRef.current.getBoundingClientRect();
        const dropdownWidth = 192;
        const left = Math.max(8, rect.right - dropdownWidth);
        setUserPosition({ top: rect.bottom, left });
      }
      if (openDropdown === 'analysis' && analysisButtonRef.current) {
        const rect = analysisButtonRef.current.getBoundingClientRect();
        setAnalysisPosition({ top: rect.bottom, left: rect.left });
      }
      if (openDropdown === 'pageMode' && pageModeButtonRef.current) {
        const rect = pageModeButtonRef.current.getBoundingClientRect();
        setPageModePosition({ top: rect.bottom, left: rect.left });
      }
      if (openDropdown === 'contentScale' && contentScaleButtonRef.current) {
        const rect = contentScaleButtonRef.current.getBoundingClientRect();
        setContentScalePosition({ top: rect.bottom, left: rect.left });
      }
      if (openDropdown === 'overflow' && overflowButtonRef.current) {
        const rect = overflowButtonRef.current.getBoundingClientRect();
        setOverflowPosition({ top: rect.bottom, left: Math.min(rect.left, window.innerWidth - 220) });
      }
      if (openDropdown === 'finalizar' && finalizarButtonRef.current) {
        const rect = finalizarButtonRef.current.getBoundingClientRect();
        setFinalizarPosition({ top: rect.bottom, left: rect.left });
      }
      if (openDropdown === 'mode' && modeButtonRef.current) {
        const rect = modeButtonRef.current.getBoundingClientRect();
        setModePosition({ top: rect.bottom, left: rect.left });
      }
      if (openDropdown === 'projeto' && projetoButtonRef.current) {
        const rect = projetoButtonRef.current.getBoundingClientRect();
        setProjetoPosition({ top: rect.bottom, left: rect.left });
      }
      if (openDropdown === 'exibir' && exibirButtonRef.current) {
        const rect = exibirButtonRef.current.getBoundingClientRect();
        setExibirPosition({ top: rect.bottom, left: rect.left });
      }
    };
    
    // Throttle para otimizar performance durante scroll
    let scrollTimeout: NodeJS.Timeout | null = null;
    let resizeTimeout: NodeJS.Timeout | null = null;
    
    // Handler otimizado com throttle para scroll
    const handleScroll = () => {
      if (scrollTimeout) return; // Ignora se já está processando
      scrollTimeout = setTimeout(() => {
        updatePositions();
        scrollTimeout = null;
      }, 16); // ~60fps
    };
    
    // Handler otimizado com debounce para resize
    const handleResize = () => {
      if (resizeTimeout) clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(updatePositions, 100);
    };
    
    const timeoutId = setTimeout(updatePositions, 0);
    
    // Só adiciona listeners se o dropdown estiver aberto
    if (openDropdown) {
      window.addEventListener('scroll', handleScroll, { passive: true, capture: true });
      window.addEventListener('resize', handleResize);
    }
    
    return () => {
      clearTimeout(timeoutId);
      if (scrollTimeout) clearTimeout(scrollTimeout);
      if (resizeTimeout) clearTimeout(resizeTimeout);
      window.removeEventListener('scroll', handleScroll, true);
      window.removeEventListener('resize', handleResize);
    };
  }, [openDropdown]);

  // Fecha dropdowns ao clicar fora
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node) && !hoveringDropdown) {
        setOpenDropdown(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [hoveringDropdown]);

  const toggleDropdown = (name: string) => {
    setOpenDropdown(openDropdown === name ? null : name);
  };

  // Paleta de cores - 5 cores  4 tons (da mais clara à mais escura)
  const handlePostItClick = (type: PostItType) => {
    props.onAddPostIt?.(type);
    setOpenDropdown(null);
  };

  return (
    <div
      className="w-full enterprise-ribbon-zone officebar-surface enterprise-font z-[9999] text-[var(--ribbon-text-primary)]"
      ref={menuRef}
      style={{
        overflow: 'visible',
        background: 'var(--officebar-bg)',
        backgroundColor: 'var(--officebar-bg)',
        backgroundImage: 'none',
        borderBottom: '1px solid var(--border-subtle, rgba(0,0,0,0.08))',
      }}
    >
      <div
        className="relative flex items-center justify-between gap-0 py-0"
        style={{
          height: 'var(--shell-ribbon-height, 48px)',
          overflowY: 'visible',
          overflowX: 'hidden',
          paddingRight: 'var(--shell-content-inset, 10px)',
          paddingLeft: 'var(--shell-header-inset, var(--shell-content-inset, 10px))',
        }}
      >
        
        {/* === RIBBON LEFT — 5 grupos visuais com densidade profissional === */}
        <div className="flex h-full flex-none items-center gap-0">

          {/* ── G0: ALTERNÂNCIA DE VISÃO — botão único dinâmico (Lista ⇄ Mapa) ── */}
          {props.onMapaTabChange && (
            <div className="flex h-full shrink-0 items-center justify-center"
              style={{ borderRight: '1px solid var(--border-subtle, rgba(0,0,0,0.08))', paddingRight: '8px', marginRight: '4px' }}>
              {props.activeMapaTab === 'list' ? (
                <ToolBtn
                  icon={<Map size={18} strokeWidth={1.5} />}
                  label="Mapa"
                  title="Voltar para o Mapa Visual (VSM)"
                  onClick={() => props.onMapaTabChange?.('canvas')}
                />
              ) : (
                <ToolBtn
                  icon={<TableProperties size={18} strokeWidth={1.5} />}
                  label="Lista"
                  title="Ver a Central de Fluxos (Lista)"
                  onClick={() => props.onMapaTabChange?.('list')}
                />
              )}
            </div>
          )}

          {props.mobileViewOnly ? (
            <RibbonGroup label="Zoom">
              <ToolBtn icon={<ZoomOut size={16} strokeWidth={1.5} />} label="−" onClick={props.onZoomOut} title="Afastar zoom" />
              <span className="min-w-[44px] text-center text-[10px] font-semibold tabular-nums text-[var(--text-secondary)]">
                {props.currentZoomLabel ?? '100%'}
              </span>
              <ToolBtn icon={<ZoomIn size={16} strokeWidth={1.5} />} label="+" onClick={props.onZoomIn} title="Aproximar zoom" />
              <ToolBtn
                icon={<Hand size={16} strokeWidth={1.5} />}
                label="Mover"
                onClick={props.onTogglePanMode}
                active={props.panModeActive}
                title="Mover canvas"
              />
              <ToolBtn icon={<Maximize2 size={16} strokeWidth={1.5} />} label="Ajustar" onClick={props.onResetZoom} title="Ajustar à tela" />
            </RibbonGroup>
          ) : (
          <>
          {/* ── G1: ARQUIVO — structural block aligned to OfficeSidebar rail ── */}
          <div className="flex h-full shrink-0 items-center justify-center"
            style={{ borderRight: '1px solid var(--border-subtle, rgba(0,0,0,0.08))', paddingRight: '8px' }}>
            <div
              className="relative"
              ref={fileButtonRef}
              onMouseEnter={() => setOpenDropdown('file')}
              onMouseLeave={() => setOpenDropdown(null)}
            >
              <ToolBtn
                icon={<FolderOpen size={18} strokeWidth={1.5} />}
                label="Arquivo"
                hasDropdown
                onClick={() => toggleDropdown('file')}
              />
              {openDropdown === 'file' && createPortal(
                <div 
                  className={dropdownPanelClass}
                  style={{ position: 'fixed', top: `${filePosition.top}px`, left: `${filePosition.left}px`, width: '220px' }}
                  onMouseEnter={() => setHoveringDropdown(true)}
                  onMouseLeave={() => setHoveringDropdown(false)}
                >
                  <button onClick={() => { props.onNewMap?.(); setOpenDropdown(null); }} disabled={props.readOnly} className={`${dropdownBaseButtonClass} ${dropdownActiveButtonClass} flex items-center${props.readOnly ? ' opacity-40 cursor-not-allowed' : ''}`}>
                    <DI icon={<FilePlus strokeWidth={1.5} />} />
                    Novo Mapa
                  </button>

                  {/* Submenu: Abrir */}
                  <SubMenuItem
                    label="Abrir"
                    icon={<FolderOpen size={13} strokeWidth={1.5} />}
                    onCloseAll={() => setOpenDropdown(null)}
                  >
                    <button onClick={() => { props.onOpenLocal?.(); setOpenDropdown(null); }} className={`${dropdownBaseButtonClass} flex items-center`}>
                      <DISpacer />
                      Abrir Arquivo (.wf)
                    </button>
                    <button onClick={() => { props.onProjectSettings?.(); setOpenDropdown(null); }} className={`${dropdownBaseButtonClass} flex items-center`}>
                      <DISpacer />
                      Abrir Projeto
                    </button>
                  </SubMenuItem>

                  {/* Submenu: Salvar */}
                  <SubMenuItem
                    label="Salvar"
                    icon={<Save size={13} strokeWidth={1.5} />}
                    onCloseAll={() => setOpenDropdown(null)}
                  >
                    <button onClick={() => { props.onSave?.(); setOpenDropdown(null); }} disabled={props.readOnly} className={`${dropdownBaseButtonClass} flex items-center${props.readOnly ? ' opacity-40 cursor-not-allowed' : ''}`}>
                      <DISpacer />
                      Salvar
                    </button>
                    <button onClick={() => { props.onSaveLocal?.(); setOpenDropdown(null); }} disabled={props.readOnly} className={`${dropdownBaseButtonClass} flex items-center${props.readOnly ? ' opacity-40 cursor-not-allowed' : ''}`}>
                      <DISpacer />
                      Salvar Como (.wf)
                    </button>
                  </SubMenuItem>

                  <DropdownSeparator />

                  {/* Submenu: Exportar */}
                  <SubMenuItem
                    label="Exportar"
                    icon={<FileDown size={13} strokeWidth={1.5} />}
                    onCloseAll={() => setOpenDropdown(null)}
                  >
                    <button onClick={() => { props.onExportPDF?.(); setOpenDropdown(null); }} className={`${dropdownBaseButtonClass} flex items-center`}>
                      <DISpacer />
                      Exportar PDF
                    </button>
                    <button onClick={() => { props.onExportImage?.(); setOpenDropdown(null); }} className={`${dropdownBaseButtonClass} flex items-center`}>
                      <DISpacer />
                      Exportar PNG
                    </button>
                    <button onClick={() => { props.onExportExcel?.(); setOpenDropdown(null); }} className={`${dropdownBaseButtonClass} flex items-center`}>
                      <DISpacer />
                      Exportar Excel
                    </button>
                  </SubMenuItem>

                  {/* Submenu: Importar */}
                  <SubMenuItem
                    label="Importar"
                    icon={<FileUp size={13} strokeWidth={1.5} />}
                    onCloseAll={() => setOpenDropdown(null)}
                  >
                    <button onClick={() => { props.onImportExcel?.(); setOpenDropdown(null); }} disabled={props.readOnly} className={`${dropdownBaseButtonClass} flex items-center${props.readOnly ? ' opacity-40 cursor-not-allowed' : ''}`}>
                      <DISpacer />
                      Importar Excel
                    </button>
                    <button onClick={() => { props.onDownloadTemplate?.(); setOpenDropdown(null); }} className={`${dropdownBaseButtonClass} flex items-center`}>
                      <DISpacer />
                      {isFactoryMap ? 'Baixar Template Factory VSM' : 'Baixar Template'}
                    </button>
                  </SubMenuItem>

                  {/* Submenu: Relatórios */}
                  <SubMenuItem
                    label="Relatórios"
                    icon={<FileText size={13} strokeWidth={1.5} />}
                    onCloseAll={() => setOpenDropdown(null)}
                  >
                    {props.onOpenReportStudio && (
                      <>
                        <button onClick={() => { props.onOpenReportStudio?.(); setOpenDropdown(null); }} className={`${dropdownBaseButtonClass} flex items-center`}>
                          <DI icon={<LayoutPanelLeft strokeWidth={1.5} />} />
                          Report Studio (WYSIWYG)
                        </button>
                        <div className="my-1 h-px mx-2 bg-[var(--border-subtle)]" />
                      </>
                    )}
                    <button onClick={() => { props.onExportEnterpriseReport?.(); setOpenDropdown(null); }} className={`${dropdownBaseButtonClass} flex items-center`}>
                      <DISpacer />
                      Relatório Enterprise
                    </button>
                    <button onClick={() => { props.onExportTechnicalFlowReport?.(); setOpenDropdown(null); }} className={`${dropdownBaseButtonClass} flex items-center`}>
                      <DISpacer />
                      Relatório Técnico do Fluxo
                    </button>
                    <button onClick={() => { props.onExportA3?.(); setOpenDropdown(null); }} className={`${dropdownBaseButtonClass} flex items-center`}>
                      <DISpacer />
                      Relatório A3
                    </button>
                    <button onClick={() => { props.onExportLeanReport?.(); setOpenDropdown(null); }} className={`${dropdownBaseButtonClass} flex items-center`}>
                      <DISpacer />
                      Relatório VSM
                    </button>
                  </SubMenuItem>

                  <DropdownSeparator />

                  <button onClick={() => { props.onVisualSettings?.(); setOpenDropdown(null); }} className={`${dropdownBaseButtonClass} flex items-center`}>
                    <DI icon={<Settings2 strokeWidth={1.5} />} />
                    Configurações
                  </button>
                  <button onClick={() => { props.onOrgManager?.(); setOpenDropdown(null); }} className={`${dropdownBaseButtonClass} flex items-center`}>
                    <DI icon={<Users strokeWidth={1.5} />} />
                    Organização
                  </button>
                </div>,
                document.body
              )}
            </div>
            <ToolBtn
              icon={<Save size={18} strokeWidth={1.5} />}
              label="Salvar"
              onClick={props.onSave}
              disabled={props.readOnly || saveStatus === 'saved'}
              title="Salvar (Ctrl+S)"
            />
          </div>

          {/* ── G2: EDIÇÃO ── */}
          <RibbonGroup label="Edição">
            <ToolBtn icon={<Undo size={16} strokeWidth={1.5} />} label="Desfazer" onClick={props.onUndo} disabled={!props.onUndo} title="Desfazer (Ctrl+Z)" />
            <ToolBtn icon={<Redo size={16} strokeWidth={1.5} />} label="Refazer" onClick={props.onRedo} disabled={!props.onRedo} title="Refazer (Ctrl+Y)" />
            <ToolBtn icon={<Copy size={15} strokeWidth={1.5} />} label="Duplicar" onClick={props.onDuplicateSelected} disabled={props.readOnly || (!props.selectedCardIds?.size && !props.selectedCardId)} title="Duplicar seleção" />
            <ToolBtn icon={<Trash2 size={15} strokeWidth={1.5} />} label="Excluir" onClick={props.onDeleteSelected} disabled={props.readOnly || (!props.selectedCardIds?.size && !props.selectedCardId)} title="Excluir seleção" />
          </RibbonGroup>

          {/* ── G4: DESIGN (dropdown unificado) ── */}
          <RibbonGroup label="Design">
            <div
              className="relative"
              ref={layoutButtonRef}
              onMouseEnter={() => setOpenDropdown('layout')}
              onMouseLeave={() => setOpenDropdown(null)}
            >
              <ToolBtn icon={<LayoutTemplate size={16} strokeWidth={1.5} />} hasDropdown onClick={() => toggleDropdown('layout')} label="Design" />
              {openDropdown === 'layout' && createPortal(
                <div
                  className={premiumPanelClass}
                  style={{ position: 'fixed', top: `${layoutPosition.top}px`, left: `${layoutPosition.left}px`, width: '220px' }}
                  onMouseEnter={() => setHoveringDropdown(true)}
                  onMouseLeave={() => setHoveringDropdown(false)}
                >
                  {props.mapType === 'FACTORY' ? (
                    <>
                      <PremiumSectionHeader>Estilo VSM</PremiumSectionHeader>
                      {(['sketch', 'blueprint', 'technical'] as const).map(style => {
                        const labels = { sketch: 'Sketch', blueprint: 'Blueprint', technical: 'Técnico' } as const;
                        const icons = { sketch: <Pencil strokeWidth={1.5} />, blueprint: <Compass strokeWidth={1.5} />, technical: <Ruler strokeWidth={1.5} /> } as const;
                        return (
                          <PremiumStyleItem
                            key={style}
                            title={labels[style]}
                            icon={icons[style]}
                            active={props.vsmDesignStyle === style}
                            onClick={() => { props.onVsmDesignStyleChange?.(style); setOpenDropdown(null); }}
                          />
                        );
                      })}
                      <DropdownSeparator />
                      <PremiumSectionHeader>Aparência</PremiumSectionHeader>
                      <PremiumColorAccordion
                        label="Cor dos Elementos"
                        currentColor={props.vsmCardColor}
                        colors={[
                          '#7dd3fc','#60a5fa','#3b82f6','#1d4ed8','#38bdf8','#06b6d4',
                          '#14b8a6','#34d399','#10b981','#4ade80','#facc15','#fb923c',
                          '#f87171','#ef4444','#e879f9','#a78bfa','#8b5cf6','#94a3b8',
                          '#e2e8f0','#ffffff',
                        ]}
                        onSelect={(color) => { props.onVsmCardColorChange?.(color); setOpenDropdown(null); }}
                      />
                    </>
                  ) : (
                    <>
                      <PremiumSectionHeader>Estilo de Card</PremiumSectionHeader>
                      {(['default', 'postit', 'minimal', 'classic', 'neon', 'blueprint', 'origami', 'kanban', 'ticket'] as const).map(layout => {
                        const labels: Record<string, string> = { default: 'Ficha', postit: 'Post-it', minimal: 'Compacto', classic: 'VSM', neon: 'Neon', blueprint: 'Blueprint', origami: 'Origami', kanban: 'Kanban', ticket: 'Ticket' };
                        const icons: Record<string, React.ReactNode> = { default: <Contact strokeWidth={1.5} />, postit: <StickyNote strokeWidth={1.5} />, minimal: <Minimize2 strokeWidth={1.5} />, classic: <Factory strokeWidth={1.5} />, neon: <Zap strokeWidth={1.5} />, blueprint: <Compass strokeWidth={1.5} />, origami: <Box strokeWidth={1.5} />, kanban: <KanbanSquare strokeWidth={1.5} />, ticket: <Ticket strokeWidth={1.5} /> };
                        return (
                          <PremiumStyleItem
                            key={layout}
                            title={labels[layout]}
                            icon={icons[layout]}
                            active={props.currentLayout === layout}
                            onClick={() => { props.onLayoutChange?.(layout); setOpenDropdown(null); }}
                          />
                        );
                      })}
                      <DropdownSeparator />
                      <PremiumSectionHeader>Aparência</PremiumSectionHeader>
                      <PremiumColorAccordion
                        label="Cor dos Elementos"
                        currentColor={props.currentColor}
                        colors={[
                          '#ffffff','#f8fafc','#f1f5f9','#e2e8f0','#cbd5e1','#94a3b8',
                          '#fef9c3','#fde68a','#fbbf24','#f59e0b','#d97706','#92400e',
                          '#dcfce7','#86efac','#4ade80','#22c55e','#16a34a','#166534',
                          '#dbeafe','#93c5fd','#3b82f6','#2563eb','#1d4ed8','#1e3a5f',
                          '#ede9fe','#c4b5fd','#8b5cf6','#7c3aed','#6d28d9','#4c1d95',
                          '#fce7f3','#f9a8d4','#ec4899','#db2777','#be185d','#9d174d',
                          '#475569','#334155','#1e293b','#0f172a','#020617','#000000',
                        ]}
                        onSelect={(color) => { props.onColorChange?.(color); setOpenDropdown(null); }}
                        onCustom={() => { colorInputRef.current?.click(); }}
                      />
                    </>
                  )}
                </div>,
                document.body
              )}
              <input
                ref={colorInputRef}
                type="color"
                className="sr-only"
                value={props.currentColor?.startsWith('#') ? props.currentColor : '#3b82f6'}
                onChange={(e) => props.onColorChange?.(e.target.value)}
              />
            </div>
          </RibbonGroup>

          {/* ── G4b: VISUALIZAÇÃO (dropdown) ── */}
          <RibbonGroup label="Visualização">
            <div
              ref={exibirButtonRef}
              className="relative"
              onMouseEnter={() => setOpenDropdown('exibir')}
              onMouseLeave={() => setOpenDropdown(null)}
            >
              <ToolBtn icon={<Layers size={15} strokeWidth={1.5} />} hasDropdown onClick={() => toggleDropdown('exibir')} label="Exibir" />
              {openDropdown === 'exibir' && createPortal(
                <div
                  className={dropdownPanelClass}
                  style={{ position: 'fixed', top: `${exibirPosition.top}px`, left: `${exibirPosition.left}px`, width: '220px' }}
                  onMouseEnter={() => setHoveringDropdown(true)}
                  onMouseLeave={() => setHoveringDropdown(false)}
                >
                  <DropdownSectionTitle>Auxílio ao Desenho</DropdownSectionTitle>
                  <button onClick={() => { props.onToggleGrid?.(); setOpenDropdown(null); }} className={`${dropdownBaseButtonClass} flex items-center ${props.gridActive ? dropdownActiveButtonClass : dropdownInactiveButtonClass}`}><DI icon={<Grid3X3 strokeWidth={1.5} />} />Grade (Grid)</button>
                  <button onClick={() => { props.onToggleRulers?.(); setOpenDropdown(null); }} className={`${dropdownBaseButtonClass} flex items-center ${props.rulersActive ? dropdownActiveButtonClass : dropdownInactiveButtonClass}`}><DI icon={<Ruler strokeWidth={1.5} />} />Régua</button>
                  <button onClick={() => { props.onToggleSnap?.(); setOpenDropdown(null); }} className={`${dropdownBaseButtonClass} flex items-center ${props.snapActive ? dropdownActiveButtonClass : dropdownInactiveButtonClass}`}><DI icon={<Magnet strokeWidth={1.5} />} />Ímã de Snap</button>
                  <button onClick={() => { props.onResetZoom?.(); setOpenDropdown(null); }} className={`${dropdownBaseButtonClass} flex items-center`}>
                    <DI icon={<Maximize2 strokeWidth={1.5} />} />
                    <span className="flex-1 text-left">Ajustar à Tela</span>
                    {props.currentZoomLabel && (
                      <span className="text-slate-400 text-[11px] tabular-nums">{props.currentZoomLabel}</span>
                    )}
                  </button>
                  <button onClick={() => { props.onZoomIn?.(); }} className={`${dropdownBaseButtonClass} flex items-center`}><DI icon={<ZoomIn strokeWidth={1.5} />} />Aproximar Zoom</button>
                  <button onClick={() => { props.onZoomOut?.(); }} className={`${dropdownBaseButtonClass} flex items-center`}><DI icon={<ZoomOut strokeWidth={1.5} />} />Afastar Zoom</button>
                  <button onClick={() => { props.onTogglePanMode?.(); setOpenDropdown(null); }} className={`${dropdownBaseButtonClass} flex items-center ${props.panModeActive ? dropdownActiveButtonClass : dropdownInactiveButtonClass}`}><DI icon={<Hand strokeWidth={1.5} />} />Mover Canvas</button>
                  <button onClick={() => { props.onToggleMinimap?.(); setOpenDropdown(null); }} className={`${dropdownBaseButtonClass} flex items-center ${props.minimapActive ? dropdownActiveButtonClass : dropdownInactiveButtonClass}`} title="Exibir/ocultar minimap e controles de zoom"><DI icon={<Map strokeWidth={1.5} />} />Minimap</button>
                  <button onClick={() => { props.onToggleExecutiveView?.(); setOpenDropdown(null); }} className={`${dropdownBaseButtonClass} flex items-center ${props.executiveViewActive ? dropdownActiveButtonClass : dropdownInactiveButtonClass}`}><DI icon={<PanelBottom strokeWidth={1.5} />} />Barra Office</button>
                  <DropdownSeparator />
                  <DropdownSectionTitle>Página</DropdownSectionTitle>
                  <button onClick={() => { setOpenDropdown('pageMode'); }} className={`${dropdownBaseButtonClass} flex items-center ${props.pageModeActive ? dropdownActiveButtonClass : dropdownInactiveButtonClass}`}><DI icon={<FileText strokeWidth={1.5} />} />Modo Página</button>
                  <SubMenuItem
                    label="Tamanho do Texto"
                    icon={<Type strokeWidth={1.5} />}
                    onCloseAll={() => setOpenDropdown(null)}
                  >
                    {[8, 9, 10, 11, 12, 14, 16, 18, 20, 24].map(size => (
                      <DropdownItem key={size} onClick={() => { props.onContentScaleChange?.(size); setOpenDropdown(null); }} active={props.contentScale === size} rightSlot={`${size}px`} monoRight>
                        {size}
                      </DropdownItem>
                    ))}
                  </SubMenuItem>
                  <DropdownSeparator />
                  <DropdownSectionTitle>Controles de Fábrica</DropdownSectionTitle>
                  <button
                    onClick={() => { props.onToggleJourneySidebar?.(); setOpenDropdown(null); }}
                    className={`${dropdownBaseButtonClass} flex items-center ${props.isJourneySidebarOpen ? dropdownActiveButtonClass : dropdownInactiveButtonClass}`}
                    title="Exibir/ocultar painel de jornada do cliente"
                  >
                    <DI icon={<Compass strokeWidth={1.5} />} />
                    <span className="flex-1 text-left">Exibir Jornada</span>
                    {props.journeyDeviationAlert && (
                      <AlertCircle size={11} strokeWidth={2} className="shrink-0 text-amber-500" aria-label="Desvio detectado na jornada" />
                    )}
                  </button>
                  <button
                    onClick={() => { props.onOpenFactorySettings?.(); setOpenDropdown(null); }}
                    className={`${dropdownBaseButtonClass} flex items-center`}
                    title="Abrir parâmetros da fábrica (Takt, turnos, OEE…)"
                  >
                    <DI icon={<Settings2 strokeWidth={1.5} />} />Configurações de Fábrica
                  </button>
                </div>,
                document.body
              )}
            </div>
          </RibbonGroup>


          {/* ── G5: ANÁLISE ── */}
          <RibbonGroup label="Análise">
            <div
              ref={projetoButtonRef}
              className="relative"
              onMouseEnter={() => setOpenDropdown('projeto')}
              onMouseLeave={() => setOpenDropdown(null)}
            >
              <ToolBtn icon={<BarChart3 size={15} strokeWidth={1.5} />} hasDropdown onClick={() => toggleDropdown('projeto')} label="Análise" />
              {openDropdown === 'projeto' && createPortal(
                <div
                  className={dropdownPanelClass}
                  style={{ position: 'fixed', top: `${projetoPosition.top}px`, left: `${projetoPosition.left}px`, width: '220px' }}
                  onMouseEnter={() => setHoveringDropdown(true)}
                  onMouseLeave={() => setHoveringDropdown(false)}
                >
                  <DropdownSectionTitle>Simulação</DropdownSectionTitle>
                  <button onClick={() => { props.onToggleSimulation?.(); setOpenDropdown(null); }} className={`${dropdownBaseButtonClass} flex items-center ${props.simulationActive ? dropdownActiveButtonClass : dropdownInactiveButtonClass}`}><DI icon={<PlayCircle strokeWidth={1.5} />} />{isFactoryMap ? 'Simular Capacidade' : 'Simular Fluxo'}</button>
                  <DropdownSeparator />
                  <DropdownSectionTitle>Indicadores</DropdownSectionTitle>
                  <button onClick={() => { props.onToggleWidget?.(); setOpenDropdown(null); }} className={`${dropdownBaseButtonClass} flex items-center ${props.widgetActive ? dropdownActiveButtonClass : dropdownInactiveButtonClass}`}><DI icon={<BarChart3 strokeWidth={1.5} />} />KPIs</button>
                  <button onClick={() => { props.onShowYamazumi?.(); setOpenDropdown(null); }} className={`${dropdownBaseButtonClass} flex items-center ${props.yamazumiActive ? dropdownActiveButtonClass : dropdownInactiveButtonClass}`}><DI icon={<Scale strokeWidth={1.5} />} />Yamazumi</button>
                  <button onClick={() => { props.onToggleHeatmap?.(); setOpenDropdown(null); }} className={`${dropdownBaseButtonClass} flex items-center ${props.heatmapActive ? dropdownActiveButtonClass : dropdownInactiveButtonClass}`}><DI icon={<Flame strokeWidth={1.5} />} />{isFactoryMap ? 'Heatmap Takt/CT' : 'Heatmap Ocupação'}</button>
                  <button onClick={() => { props.onToggleLiveMode?.(); setOpenDropdown(null); }} className={`${dropdownBaseButtonClass} flex items-center ${props.liveModeActive ? dropdownActiveButtonClass : dropdownInactiveButtonClass}`}><DI icon={<Activity strokeWidth={1.5} />} />Tempo Real</button>
                  <DropdownSeparator />
                  <DropdownSectionTitle>Comparar</DropdownSectionTitle>
                  {(props.comparisonAvailable || !isFactoryMap) && (
                    <button onClick={() => { props.onCompareScenarios?.(); setOpenDropdown(null); }} className={`${dropdownBaseButtonClass} flex items-center ${props.comparisonActive ? dropdownActiveButtonClass : ''}`}><DI icon={<GitMerge strokeWidth={1.5} />} />Comparar Cenários</button>
                  )}
                  <button
                    onClick={() => { if (props.linkedBlueprintId) { props.onShowComparePanel?.(); } setOpenDropdown(null); }}
                    disabled={!props.linkedBlueprintId}
                    className={`${dropdownBaseButtonClass} flex items-center ${props.comparePanelActive ? dropdownActiveButtonClass : dropdownInactiveButtonClass} disabled:cursor-not-allowed disabled:opacity-35`}
                  ><DI icon={<GitMerge strokeWidth={1.5} />} />Comparar Execução</button>
                </div>,
                document.body
              )}
            </div>
          </RibbonGroup>
          </>
          )}

        </div> 
        {/* FIM RIBBON LEFT */}

        {/* === RIBBON RIGHT — Filtros | Busca | Exportar | Configurações === */}
        <div className="flex shrink-0 items-center gap-0 ml-auto">

          {props.filtersSlot}

          {/* Busca com navegação */}
          {!props.mobileViewOnly && (
          <div className="flex items-center gap-0.5 mr-2">
            <div className="flex w-[220px] items-center gap-2 rounded-[3px] border border-[var(--border-default)] bg-[var(--bg-surface)] px-2">
              <Search size={14} className="shrink-0 text-[var(--text-secondary)]" />
              <input
                type="text"
                value={effectiveSearch}
                onChange={e => {
                  if (!isSearchControlled) setRibbonSearch(e.target.value);
                  props.onSearch?.(e.target.value);
                }}
                onKeyDown={e => {
                  if (e.key === 'Enter') { e.preventDefault(); props.onSearchNext?.(); }
                  if (e.key === 'Escape') {
                    e.preventDefault();
                    if (!isSearchControlled) setRibbonSearch('');
                    props.onSearch?.('');
                    props.onSearchClear?.();
                  }
                }}
                placeholder={props.searchPlaceholder ?? 'Buscar elementos…'}
                className={`w-full ${desktopRibbonInputClass} border-0 bg-transparent px-0`}
              />
              {effectiveSearch && (props.searchResultCount ?? 0) === 0 && (
                <button
                  onClick={() => {
                    if (!isSearchControlled) setRibbonSearch('');
                    props.onSearch?.('');
                    props.onSearchClear?.();
                  }}
                  className="shrink-0 rounded p-0.5 hover:bg-[var(--button-hover)]"
                ><X size={10} className="text-[var(--text-secondary)]" /></button>
              )}
            </div>
            {(props.searchResultCount ?? 0) > 0 && (
              <>
                <span className="text-[10px] text-[var(--text-secondary)] px-1 tabular-nums whitespace-nowrap">
                  {(props.searchResultIndex ?? 0) + 1}/{props.searchResultCount}
                </span>
                <button
                  onClick={props.onSearchPrev}
                  title="Anterior (Shift+Enter)"
                  className="flex items-center justify-center w-[22px] h-[26px] rounded-[3px] hover:bg-[var(--bg-elevated)] text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-colors"
                ><ChevronLeft size={13} /></button>
                <button
                  onClick={props.onSearchNext}
                  title="Próximo (Enter)"
                  className="flex items-center justify-center w-[22px] h-[26px] rounded-[3px] hover:bg-[var(--bg-elevated)] text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-colors"
                ><ChevronRight size={13} /></button>
                <button
                  onClick={() => {
                    if (!isSearchControlled) setRibbonSearch('');
                    props.onSearch?.('');
                    props.onSearchClear?.();
                  }}
                  title="Limpar busca (Esc)"
                  className="flex items-center justify-center w-[22px] h-[26px] rounded-[3px] hover:bg-[var(--bg-elevated)] text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-colors"
                ><X size={11} /></button>
              </>
            )}
          </div>
          )}

          {/* Página dropdown (via Visualização → Página) */}
          {openDropdown === 'pageMode' && createPortal(
            <div
              className={dropdownPanelClass}
              style={{ position: 'fixed', top: `${exibirPosition.top}px`, left: `${Math.min(exibirPosition.left + 220, window.innerWidth - 220)}px`, width: '220px' }}
              onMouseEnter={() => setHoveringDropdown(true)}
              onMouseLeave={() => setHoveringDropdown(false)}
            >
              <DropdownItem active={props.pageModeActive && props.pageFormat === 'a4-portrait'} onClick={() => { props.onTogglePageMode?.('a4-portrait'); setOpenDropdown(null); }} rightSlot="210×297" monoRight>A4 Retrato</DropdownItem>
              <DropdownItem active={props.pageModeActive && props.pageFormat === 'a4-landscape'} onClick={() => { props.onTogglePageMode?.('a4-landscape'); setOpenDropdown(null); }} rightSlot="297×210" monoRight>A4 Paisagem</DropdownItem>
              <DropdownItem active={props.pageModeActive && props.pageFormat === 'a3-portrait'} onClick={() => { props.onTogglePageMode?.('a3-portrait'); setOpenDropdown(null); }} rightSlot="297×420" monoRight>A3 Retrato</DropdownItem>
              <DropdownItem active={props.pageModeActive && props.pageFormat === 'a3-landscape'} onClick={() => { props.onTogglePageMode?.('a3-landscape'); setOpenDropdown(null); }} rightSlot="420×297" monoRight>A3 Paisagem</DropdownItem>
              <DropdownSeparator />
              <DropdownItem onClick={() => { props.onAddPageConnector?.('out'); setOpenDropdown(null); }}>Adicionar saída</DropdownItem>
              <DropdownItem onClick={() => { props.onAddPageConnector?.('in'); setOpenDropdown(null); }}>Adicionar entrada</DropdownItem>
              <DropdownSeparator />
              <DropdownItem onClick={() => { props.onTogglePageMode?.(); setOpenDropdown(null); }}>{props.pageModeActive ? 'Desativar' : 'Modo livre'}</DropdownItem>
            </div>,
            document.body
          )}

          {/* contentScale panel removed — now handled via SubMenuItem in Exibir dropdown */}

        </div>

      </div>

    </div>
  );
};


